<template lang="pug">
    PrintablePanel
      FormErrorAlert(
        message="There is a maximum limit of 500 transactions per individual."
        heading="Warning"
        :dismissable="false"
        v-if="transactions.length > 500 && !printable"
      )
      ICEAlert
      PageHeader Search Results - Person Summary
      .row.pt-2
        .col-md-2.col-lg-4
          .h2 {{ printName }}
          .body-1 Date of Birth:
            h5 {{ names[0] ? names[0].birthDt : '' }}
        .col.button-bar(v-if="!printable")
          //- router-link.btn.btn-primary(:to="{name: 'proximity_results', params: {id: $route.params.id}}") Proximity Search
          button.btn.btn-primary(@click="addWatch") Create Watch
          router-link.btn.btn-primary(:to="{query: {print: true}}" target="_blank") Print
      .row.bio-history-section
        .col
          h4
            | BIOGRAPHICAL HISTORY
            HelpPopover(message="This individual has also used the following biographical information in the past. Any numerical information beginning with zeros has had those removed for display purposes on the Person Summary page, but full details can be found by clicking on the Related Records. Most recent biographical information is presented first.")
          Datatable(class="bio-history"
                    :entries="[0]"
                    tableKey="bioTable"
                    :sortable="false"
                    :perPage="1"
                    :footer="false"
                    :header-style="{fontSize: '0.75rem'}")
            template(slot="header")
              td.h6 Name
                input.select-all(v-if="$auth.internalAdmin() && !printable && uniqueNames.length" type="checkbox" v-model="allNamesSelected")
              td.h6 Date of Birth
                input.select-all(v-if="$auth.internalAdmin() && !printable && uniqueBirthDts.length" type="checkbox" v-model="allDobsSelected")
              td.h6 ID Information
                input.select-all(v-if="$auth.internalAdmin() && !printable && nonNullAndEmptyIds.length" type="checkbox" v-model="allIdsSelected")
              td.h6 Address
            template(slot-scope="props")
              tr(v-if="printable || !$auth.internalAdmin()")
                td.body-3
                  div(v-for="name in uniqueNames") {{ name.lastName }}, {{ name.firstName }} {{ name.middleName }}
                td.body-3
                  div(v-for="dob in uniqueBirthDts") {{ dob }}
                td.body-3
                  div(v-for="id in nonNullAndEmptyIds" ) {{ id.idNbr }} / {{ id.idStateCd }} / {{ idType(id.idTypeCd) }}
                td.body-3
                  div(v-for="address in addresses")
                    div {{ address.address1 }}
                    div {{ address.address2 }}
                    div {{ address.city }}, {{ address.stateCd }} {{ address.postalCode}}
              tr(v-else)
                td.body-3
                  div(class="checkboxes" v-for="name in uniqueNames")
                    label {{ name.lastName }}, {{ name.firstName }} {{ name.middleName }}
                      input(v-if="name.lastName" class="align-center justify-center" type="checkbox" name="name" :value="name" v-model="checkedNames" @change="clearCheckedNameError")
                td.body-3
                  div(class="checkboxes" v-for="dob in uniqueBirthDts")
                    label {{ dob }}
                      input(type="checkbox" name="dob" :value="dob" v-model="checkedDobs" @change="clearCheckedDobError")
                td.body-3
                  div(class="checkboxes" v-for="id in nonNullAndEmptyIds")
                    label {{ id.idNbr }} / {{ id.idStateCd }} / {{ idType(id.idTypeCd) }}
                      input(type="checkbox" name="idNbr" :value="id" v-model="checkedIds" @change="clearCheckedIdError")
                td.body-3
                  div(v-for="address in addresses")
                    div {{ address.address1 }}
                    div {{ address.address2 }}
                    div {{ address.city }}, {{ address.stateCd }} {{ address.postalCode}}
              tr(id="split-buttons" v-if="$auth.internalAdmin() && !printable")
                td.body-3.buttons
                  button.btn.btn-primary(@click.prevent="splitByName") Split by Name
                  FormError(v-if="checkedNameError")
                    span(style="font-size: 0.75rem") {{ checkedNameError }}
                td.body-3.buttons
                  button.btn.btn-primary(@click.prevent="splitByDob") Split by Birth Date
                  FormError(v-if="checkedDobError")
                    span(style="font-size: 0.75rem") {{ checkedDobError }}
                td.body-3.buttons
                  button.btn.btn-primary(@click.prevent="splitById") Split by ID
                  FormError(v-if="checkedIdError")
                    span(style="font-size: 0.75rem") {{ checkedIdError }}
                td.body-3.buttons
                  button.btn.btn-primary(@click.prevent="splitTwins") Split Twins
      .row.transaction-summary
        .col
          h4 TRANSACTION SUMMARY
          .transaction-summary-options
            .row
              .col-lg-1
                .h5.filters-heading Filters
              .col-lg.summary-border
                .h7 Transaction Timeframe
                .row
                  .col-1
                    CustomRadio(v-model="filterTimeframe",modelValue="timeframe")
                  .col.transaction-timeframe-label.body-3
                    div
                      label(for="timeframe-start") From
                      br
                      Datepicker#timeframe-start(v-model="filterFromDate"
                                                  input-placeholder="mm/dd/yyyy"
                                                  :input-class="{'is-invalid': false}")
                    div
                      label(for="timeframe-end") To
                      br
                      Datepicker#timeframe-end(v-model="filterToDate"
                                                input-placeholder="mm/dd/yyyy"
                                                :input-class="{'is-invalid': false}")
                CustomRadio(v-model="filterTimeframe",modelValue="all")
                  span.body-3.ml-2 Show All Transactions ({{ trx500.length }})
              .col-lg.summary-border
                .h7 Transaction Details
                CustomCheckbox(v-model="filterShowDetails")
                  span.body-3.ml-2 Show Transaction Details and Signatures
              .col-lg
                .h7 Transaction Type
                .row
                  .col-md
                    CustomRadio(v-model="filterTransactionType",modelValue="all",name="transaction-type")
                      span.body-3.ml-2 All Activity Types ({{ allActivityCount }}) {{ allActivityGrams }}g
                  .col-md
                    CustomRadio(v-model="filterTransactionType",modelValue="Exceedance",name="transaction-type")
                      span.body-3.ml-2 Exceedances ({{ exceedanceActivityCount }}) {{ exceedanceActivityGrams }}g
                .row
                  .col-md
                    CustomRadio(v-model="filterTransactionType",modelValue="Purchase",name="transaction-type")
                      span.body-3.ml-2 Purchases ({{ purchaseActivityCount }}) {{ purchaseActivityGrams }}g
                  .col-md
                    CustomRadio(v-model="filterTransactionType",modelValue="Return",name="transaction-type")
                      span.body-3.ml-2 Returns ({{ returnActivityCount }}) {{ returnActivityGrams }}g
                .row
                  .col-md
                    CustomRadio(v-model="filterTransactionType",modelValue="Block",name="transaction-type")
                      span.body-3.ml-2 Blocks ({{ blockActivityCount }}) {{ blockActivityGrams }}g
          Datatable.mt-3(:columns="summaryColumns"
                          :entries="filteredTransactions"
                          :sortable="false"
                          tableKey="summaryTable"
                          :footer="trx500.length > 6"
                          :perPage="6"
                          :header-style="{fontSize: '0.75rem'}")
            template(slot-scope="props")
              tr
                td.body-3
                  div(v-if="props.entry.subjectHistory") (N) {{ props.entry.transactionSid }}
                  div(v-else) (N) {{ props.entry.transactionSid + '*'}}
                  div (S) {{ props.entry.pharmacyTrxId || 'N/A' }}
                td.body-3 {{ props.entry.transactionDt }}
                td.body-3 {{ props.entry.transactionCd }}
                td.body-3 {{ props.entry.productDesc.join(', ') }}
                td.body-3 {{ props.entry.grams }}
                td.body-3 {{ props.entry.boxes }}
                td.body-3
                  strong(v-if="props.entry.blockListBlock") Blocked by {{ props.entry.searchStateList }} blocklist.
                  div(v-else v-for="(v, k) in props.entry.complianceFailType").mb-2
                    div
                      strong {{ k }}:
                    div {{ v.join(', ') }}
                td.body-3.pharmacy-cell
                  div
                    a(
                      href=""
                      @click.prevent="getStoreActivityReport(props.entry.pharmacySid, props.entry.transactionDt)"
                    ) {{ props.entry.pharmacyName  }}
                  div {{ props.entry.pharmacyPhone }}
                  div {{ props.entry.pharmacyAddress1 }}
                  div {{ props.entry.pharmacyAddress2 }}
                  div {{ props.entry.pharmacyCity }}, {{ props.entry.pharmacyStateCd }} {{ props.entry.pharmacyPostalCode }}
              tr(v-if="filterShowDetails")
                td.body-3(colspan="8")
                  .row
                    .col-3
                      div
                        em Transaction Details
                      div {{ props.entry.firstName }} {{ props.entry.lastName }}
                      div {{ props.entry.address1 }}
                      div {{ props.entry.address2 }}
                      div {{ props.entry.city }}, {{ props.entry.postalStateCd }} {{ props.entry.postalCode }}
                      div {{ props.entry.birthDt }}
                      div(v-if="props.entry.idNbr") {{ props.entry.idNbr }} - {{ props.entry.idStateCd }} - {{ idType(props.entry.idTypeCd) }}
                      div ID Expires: {{ props.entry.idExpDt || 'N/A' }}
                    .col
                      img.img-fluid(
                        v-if="imageUrls[props.entry.imageName]"
                        :src="'data:image/png;base64, ' + imageUrls[props.entry.imageName]"
                        style="border: 1px solid #e6e6e6;"
                        @load="() => imagesLoaded.push(props.entry.imageName)"
                        @error="() => imagesLoaded.push(props.entry.imageName)")
      .row
        .col
          .h7(v-if="showConsolidationWarningCheck") *Due to compliance/consolidation history, this transaction is shown.
      .row.related-persons(v-if="!printable")
        .col
          h4 RELATED PERSONS
          Datatable(:columns="relatedPersonColumns"
                    :entries="related"
                    :sortable="false"
                    tableKey="relatedPersonTable"
                    :footer="false"
                    :header-style="{fontSize: '0.75rem'}")
            template(slot-scope="props")
              tr
                td.body-2
                  router-link(:to="{name: 'person_summary', params: { id: props.entry.subjectSid }}") View Person
                td.body-3 {{ props.entry.lastName }}, {{ props.entry.firstName }}
                td.body-3
                  div {{ props.entry.address1 }}
                  div {{ props.entry.city }}, {{ props.entry.stateCd }} {{ props.entry.postalCode }}
                td.body-3 {{ props.entry.birthDt }}
      .row.watches(v-if="!printable")
        .col
          h4 BEING WATCHED BY
          Datatable(:columns="watchColumns"
                    :entries="watches"
                    table-key="watchesTable"
                    :footer="false"
                    :header-style="{fontSize: '0.75rem'}")
            template(slot-scope="props")
              tr
                td.body-3 {{ props.entry.agencies[0].agency_description }}
                td.body-3
                  | {{ props.entry.agencies[0].address1 }}
                  template(v-if="props.entry.agencies[0].address2")
                    br
                    | {{ props.entry.agencies[0].address2 }}
                  br
                  | {{ props.entry.agencies[0].city }}, {{ props.entry.agencies[0].state_cd }} {{ props.entry.agencies[0].postal_code }}
                td.body-3 {{ props.entry.agencies[0].phone_number }}
                td.body-3 {{ props.entry.lastName}}, {{ props.entry.firstName }}
                td.body-3 {{ props.entry.workPhone }}
                td.body-3 {{ props.entry.email }}
</template>

<script>
import Datatable from '../components/Datatable'
import HelpPopover from '../components/HelpPopover'
import CustomCheckbox from '@/components/CustomCheckbox'
import CustomRadio from '@/components/CustomRadio'
import Modal from '@/components/Modal'
import PageHeader from '@/components/PageHeader'
import Datepicker from '@/components/Datepicker'
import PrintablePanel from '@/components/PrintablePanel'
import axios from '@/lib/axios'
import { format, parse, addDays } from 'date-fns/esm'
import { globalError, globalSuccess, idType, waitReport } from '@/lib/utils'
import { saveItem, loadForm, clearForm } from '@/lib/formstore'

async function getData(sid) {
  let results = {}
  let promises = [
    axios()
      .get('/api/person_summary/names', {
        params: { sid },
      })
      .then(({ data }) => (results.names = data)),
    axios()
      .get('/api/person_summary/ids', {
        params: { sid },
      })
      .then(({ data }) => (results.ids = data)),
    axios()
      .get('/api/person_summary/addresses', {
        params: { sid },
      })
      .then(({ data }) => (results.addresses = data)),
    axios()
      .get('/api/person_summary/transactions', {
        params: { sid },
      })
      .then(({ data }) => (results.transactions = data)),
    axios()
      .get('/api/person_summary/related', {
        params: { sid },
      })
      .then(({ data }) => (results.related = data)),
    axios()
      .get('/api/person_summary/watches', {
        params: { sid },
      })
      .then(({ data }) => (results.watches = data)),
  ]
  try {
    await Promise.all(promises)
  } catch (err) {
    globalError(err)
  }
  return results
}

export default {
  name: 'PersonSummary',
  data: function() {
    return {
      relatedPersonColumns: ['Action', 'Name', 'Related By', 'Date of Birth'],
      watchColumns: [
        'Agency',
        'Agency Address',
        'Agency Phone',
        'Person',
        'Phone',
        'Email',
      ],
      names: [],
      ids: [],
      addresses: [],
      transactions: [],
      related: [],
      watches: [],
      filterTimeframe: 'timeframe',
      filterFromDate: this.initialFromDate(),
      filterToDate: this.initialToDate(),
      filterShowDetails: false,
      filterTransactionType: 'all',
      imageUrls: {},
      imagesLoaded: [],
      checkedNames: [],
      allNamesSelected: false,
      checkedDobs: [],
      allDobsSelected: false,
      checkedIds: [],
      allIdsSelected: false,
      checkedNameError: '',
      checkedDobError: '',
      checkedIdError: '',
      sid: '',
    }
  },
  computed: {
    summaryColumns: function() {
      return [
        'Transaction ID(s)<br/>(N)PLEx<br/>(S)tore',
        'Transaction Date',
        'Activity Type',
        'Product(s)',
        'Grams',
        'Box(es)',
        'Compliance Fail Type',
        `Pharmacy<br/><em style="font-weight: normal;">Unique Pharmacies (${
          this.uniquePharmacyCount
        })</em>`,
      ]
    },
    trx500() {
      return this.transactions.slice(0, 500)
    },
    filteredTransactions() {
      return this.trx500
        .filter(
          cd =>
            this.filterTransactionType !== 'all'
              ? cd.transactionCd === this.filterTransactionType
              : true,
        )
        .filter(trx => {
          if (this.filterTimeframe === 'all') return true
          let pTrx = parse(
            trx.transactionDt.split(' ')[0],
            'MM/dd/yyyy',
            new Date(),
          )
          let pFrom = parse(this.filterFromDate, 'MM/dd/yyyy', new Date())
          let pTo = parse(this.filterToDate, 'MM/dd/yyyy', new Date())
          return pTrx >= pFrom && pTrx <= pTo
        })
    },
    uniquePharmacyCount: function() {
      return Array.from(
        new Set(this.filteredTransactions.map(summary => summary.pharmacySid)),
      ).length
    },
    allActivityCount: function() {
      return this.filteredTransactions.length
    },
    purchaseActivityCount: function() {
      return this.filteredTransactions.filter(
        ts => ts.transactionCd === 'Purchase',
      ).length
    },
    blockActivityCount: function() {
      return this.filteredTransactions.filter(
        ts => ts.transactionCd === 'Block',
      ).length
    },
    exceedanceActivityCount: function() {
      return this.filteredTransactions.filter(
        ts => ts.transactionCd === 'Exceedance',
      ).length
    },
    returnActivityCount: function() {
      return this.filteredTransactions.filter(
        ts => ts.transactionCd === 'Return',
      ).length
    },
    allActivityGrams: function() {
      return this.filteredTransactions
        .reduce((acc, ts) => acc + Number(ts.grams), 0)
        .toFixed(2)
    },
    purchaseActivityGrams: function() {
      return this.filteredTransactions
        .filter(ts => ts.transactionCd === 'Purchase')
        .reduce((acc, ts) => acc + Number(ts.grams), 0)
        .toFixed(2)
    },
    blockActivityGrams: function() {
      return this.filteredTransactions
        .filter(ts => ts.transactionCd === 'Block')
        .reduce((acc, ts) => acc + Number(ts.grams), 0)
        .toFixed(2)
    },
    exceedanceActivityGrams: function() {
      return this.filteredTransactions
        .filter(ts => ts.transactionCd === 'Exceedance')
        .reduce((acc, ts) => acc + Number(ts.grams), 0)
        .toFixed(2)
    },
    returnActivityGrams: function() {
      return this.filteredTransactions
        .filter(ts => ts.transactionCd === 'Return')
        .reduce((acc, ts) => acc + Number(ts.grams), 0)
        .toFixed(2)
    },
    printName() {
      let n = this.names[0]
      if (!n) {
        return ''
      } else {
        return `${n['lastName'] || ''}, ${n['firstName'] || ''} ${n[
          'middleName'
        ] || ''}`
      }
    },
    printBirthDt() {
      let n = this.names[0]
      if (!n) return ''
      let bd = parse(n['birthDt'], 'MM/DD/yyyy HH:mm a', new Date())
      return format(bd, 'MM/dd/yyyy')
    },
    imageNames() {
      return this.trx500.map(t => t.imageName).filter(i => i)
    },
    uniqueBirthDts() {
      let filteredBirthDts = this.names.filter(
        n => n.birthDt !== null && n.birthDt !== '',
      )
      return [...new Set(filteredBirthDts.map(n => n['birthDt']))]
    },
    uniqueNames() {
      let filteredNames = this.names.filter(
        n =>
          (n.lastName !== null && n.lastName !== '') ||
          (n.firstName !== null && n.firstName !== '') ||
          (n.middleName !== null && n.middleName !== ''),
      )
      let noDuplicateNames = [
        ...new Set(
          filteredNames.map(
            n =>
              `${n.lastName || ''}::${n.firstName || ''}::${n.middleName ||
                ''}`,
          ),
        ),
      ]
      let namesObj = []
      for (let name of noDuplicateNames) {
        namesObj.push(
          Object.assign({
            lastName: name.split('::')[0],
            firstName: name.split('::')[1],
            middleName: name.split('::')[2],
          }),
        )
      }
      return namesObj
    },
    uniqLoaded() {
      return [...new Set(this.imagesLoaded)]
    },
    uniqUrls() {
      return [...new Set(Object.keys(this.imageUrls))]
    },
    nonNullAndEmptyIds: function() {
      return this.ids.filter(id => id.idNbr !== null && id.idNbr !== '')
    },
    showConsolidationWarningCheck() {
      let showConsolidationWarning = false
      for (let i = 0; i < this.filteredTransactions.length; i++) {
        if (!this.filteredTransactions[i].subjectHistory) {
          showConsolidationWarning = true
        }
      }
      return showConsolidationWarning
    },
  },
  watch: {
    imageNames(val) {
      let promises = []
      for (let im of val) {
        if (!im) continue
        let p = axios()
          .get('/api/misc/signature', {
            params: { path: encodeURIComponent(im) },
          })
          .then(resp => {
            this.$set(this.imageUrls, im, resp.data.encoded_img)
          })
        promises.push(p)
      }
      Promise.all(promises).finally(() => {
        let crit =
          this.printable &&
          (this.uniqUrls.length === 0 || !this.filterShowDetails)
        if (crit) window.print()
      })
    },
    imagesLoaded(val) {
      let uniqLoaded = [...new Set(val)]
      let uniqNames = [...new Set(this.imageNames)]
      if (uniqLoaded.length === uniqNames.length && this.printable) {
        window.print()
      }
    },
    '$route.params.id': function() {
      getData(this.$route.params.id)
        .then(results => {
          this.names = results.names
          this.ids = results.ids
          this.addresses = results.addresses
          this.transactions = results.transactions
          this.related = results.related
          this.watches = results.watches
        })
        .catch(err => globalError(err))
    },
    allNamesSelected: function() {
      this.checkedNames = []
      if (this.allNamesSelected) {
        for (let name in this.uniqueNames) {
          if (
            this.uniqueNames[name].lastName != '' &&
            this.uniqueNames[name].lastName != null
          ) {
            this.checkedNames.push(this.uniqueNames[name])
          }
        }
      }
    },
    allDobsSelected: function() {
      this.checkedDobs = []
      if (this.allDobsSelected) {
        for (let dob in this.uniqueBirthDts) {
          this.checkedDobs.push(this.uniqueBirthDts[dob])
        }
      }
    },
    allIdsSelected: function() {
      this.checkedIds = []
      if (this.allIdsSelected) {
        for (let id in this.nonNullAndEmptyIds) {
          this.checkedIds.push(this.nonNullAndEmptyIds[id])
        }
      }
    },
  },
  methods: {
    idType: idType,
    toggleProximitySearchId: function(id) {
      let idx = this.proximitySearchIds.indexOf(id)
      if (idx < 0) {
        this.proximitySearchIds.push(id)
      } else {
        this.proximitySearchIds.splice(idx, 1)
      }
    },
    // proximitySearch: function() {
    //   this.$router.push({
    //     name: 'proximity_results',
    //   })
    // },
    initialToDate: function() {
      return format(new Date(), 'MM/dd/yyyy')
    },
    initialFromDate: function() {
      let dt = new Date()
      dt.setFullYear(dt.getFullYear() - 1)
      return format(dt, 'MM/dd/yyyy')
    },
    addWatch() {
      const t = this.transactions[0]
      let criteria = {
        birthDt: t.birthDt,
        watchMustMatchAddress: 'N',
        sendSmsAlerts: 'N',
        notes: '',
        alertAltEmail1: true,
        alertAltEmail2: true,
        additionalEmails: [],
        names: [
          {
            firstName: t.firstName,
            lastName: t.lastName,
            middleName: t.middleName || '',
          },
        ],
        states: [],
        ids: [t.idNbr],
        addresses: [
          {
            address1: t.address1,
            address2: t.address2,
            city: t.city,
            stateCd: t.stateCd,
            postalCode: t.postalCode,
          },
        ],
      }
      axios()
        .post('/api/watches', criteria)
        .then(() =>
          globalSuccess(
            null,
            'Watch was successfully added using the latest transaction information.',
          ),
        )
        .catch(err => globalError(err))
    },
    saveClearNames() {
      let checkedNamesArray = this.checkedNames.map(
        n => `${n.lastName || ''}, ${n.firstName || ''} ${n.middleName || ''}`,
      )
      saveItem('splitByName', 'checkedNames', checkedNamesArray)
      saveItem('splitSubjectSid', 'subjectSid', this.sid)
      clearForm('splitByDob')
    },
    saveClearDobs() {
      saveItem('splitByDob', 'checkedDobs', this.checkedDobs)
      saveItem('splitSubjectSid', 'subjectSid', this.sid)
      clearForm('splitByName')
    },
    splitByName() {
      if (this.checkedNames.length > 0) {
        this.saveClearNames()
        this.$router.push({ name: 'split_by_bio' })
      } else {
        this.checkedNameError = 'Please select a name to split by.'
      }
    },
    splitByDob() {
      if (this.checkedDobs.length > 0) {
        this.saveClearDobs()
        this.$router.push({ name: 'split_by_bio' })
      } else {
        this.checkedDobError = 'Please select a date to split by.'
      }
    },
    splitById() {
      if (this.checkedIds.length > 0) {
        saveItem('splitById', 'checkedIds', this.checkedIds)
        this.$router.push({
          name: 'split_by_id',
          params: { ids: this.checkedIds },
        })
      } else {
        this.checkedIdError = 'Please select an id to split by.'
      }
    },
    splitTwins() {
      this.$router.push({
        name: 'split_twins',
        params: { id: this.sid },
      })
    },
    getStoreActivityReport(pharmacySid, transactionDt) {
      let day = transactionDt.split(' ')[0]
      axios()
        .post('/api/store_activity/search', {
          pharmacySid: pharmacySid,
          fromDate: day + ' 12:00 AM',
          toDate: day + ' 11:59 PM',
          signatures: false,
        })
        .then(resp => {
          let id = resp.data.id
          waitReport(id, { name: 'store_activity_report', params: { id } })
        })
        .catch(err => globalError(err))
    },
    clearCheckedNameError() {
      this.checkedNameError = ''
    },
    clearCheckedDobError() {
      this.checkedDobError = ''
    },
    clearCheckedIdError() {
      this.checkedIdError = ''
    },
  },
  mounted: loadForm(
    'personsummary',
    'filterTimeframe',
    'filterFromDate',
    'filterToDate',
    'filterTransactionType',
    'filterShowDetails',
  ),
  components: {
    Datatable,
    HelpPopover,
    CustomCheckbox,
    CustomRadio,
    Modal,
    PageHeader,
    Datepicker,
    PrintablePanel,
  },
  beforeRouteEnter(to, _from, next) {
    getData(to.params.id)
      .then(results => {
        scrollTo(0, 0)
        next(vm => {
          vm.names = results.names
          vm.ids = results.ids
          vm.addresses = results.addresses
          vm.transactions = results.transactions
          vm.related = results.related
          vm.watches = results.watches
          vm.sid = to.params.id
        })
      })
      .catch(err => globalError(err))
  },
  beforeRouteLeave(_to, _from, next) {
    clearForm('personsummary')
    next()
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/bodies';
@import '../assets/styles/breakpoints';
.panel-white {
  @media (min-width: $bp-xl) {
    width: 80%;
  }
  @media (max-width: $bp-xl) {
    width: 100%;
  }
}
.panel-white {
  h1 {
    border-bottom: 2px solid #e6e6e6;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }
}
.h7 {
  @media (max-width: $bp-xl) {
    margin-top: 1rem;
  }
}
.button-bar {
  display: flex;
  flex-direction: row-reverse;
  button,
  a {
    margin-right: 1rem;
    width: 10rem;
    height: 2.2rem;

    @media (max-width: $bp-xl) {
      display: block;
      margin-top: 1rem;
    }
  }
}
.bio-history-section {
  margin-top: 2rem;
}
.transaction-summary {
  margin-top: 2rem;
  label {
    @extend .body-3;
  }
}
.transaction-summary-options {
  padding: 0.8rem;
  border: 1px solid #d8d8d8;
}
.filters-heading {
  color: #707070;
}
.transaction-timeframe-label {
  display: flex;
  input {
    width: 6rem;
  }
  input:first-of-type {
    margin-right: 0.2rem;
  }
  input:last-of-type {
    margin-left: 0.2rem;
  }
}
.related-persons {
  margin-top: 2rem;
}
input.is-invalid {
  border: 1px solid red;
}

span.dash {
  display: none;
}
@media (min-width: 1400px) {
  span.dash {
    display: inline;
  }
}

.watches {
  margin-top: 2rem;
}

.summary-border {
  border-right: 1px solid #ededed;
}

#timeframe-start,
#timeframe-end {
  padding-left: 6px;
}
.pharmacy-cell {
  white-space: nowrap;
}

.checkboxes label {
  display: inline-block;
}

.checkboxes input {
  margin: 0px 0px 0px 5px;
  vertical-align: middle;
  position: relative;
  vertical-align: middle;
  bottom: 1px;
}

.text-danger {
  padding-top: 5px;
}

.datatable.bio-history {
  border: none;

  table {
    thead {
      td {
        border: solid 1px rgb(216, 216, 216);
      }
    }

    tbody {
      tr:nth-child(1) {
        border: solid 1px rgb(216, 216, 216);
      }

      tr:nth-child(2n)#split-buttons {
        background-color: white !important;
      }
    }
  }
}

td.body-3.buttons {
  border: none !important;
}

.select-all {
  float: right;
}
</style>
